import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Copy, ImagePlus, Loader2, MessageCircle, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { waLink } from "@/lib/format";
import { useSeo } from "@/lib/shop";

const field = "w-full h-11 rounded-lg border border-neutral-300 px-3 text-sm bg-white focus:border-neutral-900 focus:ring-2 focus:ring-[var(--bk-yellow)] outline-none";
const L = ({ label, children, hint }) => (
  <label className="block"><span className="text-sm font-semibold">{label}</span><div className="mt-1">{children}</div>{hint && <span className="text-xs text-neutral-500">{hint}</span>}</label>
);
const blankItem = () => ({ key: Math.random().toString(36).slice(2), name: "", brand: "", category_id: "", condition: "used", completeness: "", description: "", photos: [], ask_price: "", min_price: "", offer_price: "", note: "" });

function Photos({ photos, onChange, uploadUrl, idx }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 8 - photos.length);
    e.target.value = "";
    setBusy(true);
    const out = [...photos];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(uploadUrl, fd);
        out.push({ path: data.path, preview: URL.createObjectURL(f) });
      } catch (err) {
        toast.error(errMsg(err));
      }
    }
    onChange(out);
    setBusy(false);
  };
  return (
    <div>
      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
        {photos.map((p, i) => (
          <div key={p.path} className="relative aspect-square rounded-lg overflow-hidden border">
            <img src={p.preview} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => onChange(photos.filter((_, j) => j !== i))} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 text-white grid place-items-center" aria-label="Hapus foto"><X className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        {photos.length < 8 && (
          <label className="aspect-square rounded-lg border-2 border-dashed border-neutral-400 grid place-items-center cursor-pointer hover:bg-neutral-50" data-testid={`item-${idx}-photo-label`}>
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <span className="text-center text-xs"><ImagePlus className="h-5 w-5 mx-auto" />Tambah</span>}
            <input type="file" accept="image/*" multiple className="hidden" onChange={pick} data-testid={`item-${idx}-photo-input`} disabled={busy} />
          </label>
        )}
      </div>
      <p className={`text-xs mt-1 ${photos.length < 3 ? "text-red-600" : "text-neutral-500"}`}>{photos.length}/8 foto (minimal 3). Foto depan, belakang, dan detail kondisi.</p>
    </div>
  );
}

function ItemCard({ it, idx, type, cats, set, remove, canRemove, uploadUrl }) {
  const u = (k) => (e) => set({ ...it, [k]: e.target.value });
  return (
    <div className="rounded-2xl border-2 border-neutral-900 bg-white p-4 sm:p-5" data-testid={`item-card-${idx}`}>
      <div className="flex items-center justify-between mb-3"><b className="font-heading">Item #{idx + 1}</b>{canRemove && <button type="button" onClick={remove} className="text-sm text-red-600 inline-flex items-center gap-1" data-testid={`item-${idx}-remove`}><Trash2 className="h-4 w-4" />Hapus</button>}</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <L label="Nama produk *"><input className={field} value={it.name} onChange={u("name")} required minLength={2} data-testid={`item-${idx}-name`} /></L>
        <L label="Brand"><input className={field} value={it.brand} onChange={u("brand")} placeholder="Bandai, Hot Wheels, ..." data-testid={`item-${idx}-brand`} /></L>
        <L label="Kategori *"><select className={field} value={it.category_id} onChange={u("category_id")} required data-testid={`item-${idx}-category`}><option value="">Pilih kategori</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></L>
        <L label="Kondisi *"><select className={field} value={it.condition} onChange={u("condition")} data-testid={`item-${idx}-condition`}><option value="new">Baru</option><option value="used">Bekas</option></select></L>
        <div className="sm:col-span-2"><L label="Kelengkapan" hint="Contoh: box, manual, semua part lengkap, sticker belum dipakai"><input className={field} value={it.completeness} onChange={u("completeness")} data-testid={`item-${idx}-completeness`} /></L></div>
        <div className="sm:col-span-2"><L label={type === "sell" ? "Deskripsi kondisi" : "Deskripsi produk"}><textarea rows={3} className={`${field} h-auto py-2`} value={it.description} onChange={u("description")} data-testid={`item-${idx}-description`} /></L></div>
        {type === "consignment" ? (
          <>
            <L label="Harga jual yang diinginkan (Rp) *"><input type="number" min={1000} className={field} value={it.ask_price} onChange={u("ask_price")} required data-testid={`item-${idx}-ask-price`} /></L>
            <L label="Harga minimum (Rp) *" hint="Harga terendah yang masih kamu terima"><input type="number" min={1} className={field} value={it.min_price} onChange={u("min_price")} required data-testid={`item-${idx}-min-price`} /></L>
          </>
        ) : (
          <>
            <L label="Harga penawaran kamu (Rp) *"><input type="number" min={1000} className={field} value={it.offer_price} onChange={u("offer_price")} required data-testid={`item-${idx}-offer-price`} /></L>
            <L label="Catatan tambahan"><input className={field} value={it.note} onChange={u("note")} data-testid={`item-${idx}-note`} /></L>
          </>
        )}
        <div className="sm:col-span-2"><span className="text-sm font-semibold">Foto produk *</span><div className="mt-1"><Photos idx={idx} photos={it.photos} onChange={(photos) => set({ ...it, photos })} uploadUrl={uploadUrl} /></div></div>
      </div>
    </div>
  );
}

export function Success({ res, wa }) {
  const link = `${window.location.origin}/collection/track?s=${res.sub_no}&t=${res.token}`;
  const copy = (v) => navigator.clipboard?.writeText(v).then(() => toast.success("Disalin"));
  return (
    <div className="max-w-xl mx-auto rounded-3xl border-2 border-neutral-900 bg-white p-6 sm:p-8 shadow-[6px_6px_0_#111]" data-testid="submission-success">
      <CheckCircle2 className="h-10 w-10 text-emerald-600" />
      <h2 className="font-heading text-2xl font-extrabold mt-3">Pengajuan berhasil dikirim!</h2>
      <p className="text-sm text-neutral-600 mt-1">Simpan informasi di bawah. <b>PIN hanya ditampilkan sekali</b> dan dibutuhkan untuk melacak pengajuan.</p>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-neutral-50 border p-3"><div className="text-xs text-neutral-500">Submission ID</div><div className="font-mono font-bold text-lg" data-testid="success-sub-no">{res.sub_no}</div></div>
        <div className="rounded-xl bg-amber-50 border border-amber-300 p-3"><div className="text-xs text-neutral-500">Access PIN</div><div className="font-mono font-bold text-lg tracking-widest" data-testid="success-pin">{res.pin}</div></div>
      </div>
      <button type="button" onClick={() => copy(`Submission ID: ${res.sub_no}\nPIN: ${res.pin}\nLink: ${link}`)} className="btn-brand w-full mt-3" data-testid="success-copy"><Copy className="h-4 w-4" />Salin ID, PIN & link tracking</button>
      <Link to={`/collection/track?s=${res.sub_no}&t=${res.token}`} className="btn-ink w-full mt-3" data-testid="success-track-link">Buka Halaman Tracking</Link>
      {wa && <a href={waLink(wa, `Halo BengkelHobby, saya baru mengajukan ${res.type === "consignment" ? "titip jual" : "penjualan (Sell to Us)"} dengan nomor ${res.sub_no}.`)} target="_blank" rel="noreferrer" className="btn-wa w-full mt-3" data-testid="success-wa"><MessageCircle className="h-4 w-4" />Kabari admin via WhatsApp</a>}
    </div>
  );
}

export function SubmitFormCore({ type, endpoint = "/collection/submit", uploadUrl = "/collection/upload", onDone, assisted = false }) {
  const { data: cfg } = useQuery({ queryKey: ["mk-config"], queryFn: () => api.get("/collection/config").then((r) => r.data) });
  const [f, setF] = useState({ name: "", phone: "", email: "", handover: "store", consign_days: "", terms_accepted: assisted, website: "" });
  const [items, setItems] = useState([blankItem()]);
  const [busy, setBusy] = useState(false);
  if (!cfg) return <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  const u = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const bad = items.findIndex((it) => it.photos.length < 3);
    if (bad >= 0) return toast.error(`Item #${bad + 1} butuh minimal 3 foto`);
    setBusy(true);
    try {
      const body = { ...f, type, consign_days: Number(f.consign_days) || 0, items: items.map(({ key, photos, ...it }) => ({ ...it, photos: photos.map((p) => p.path), ask_price: Number(it.ask_price) || 0, min_price: Number(it.min_price) || 0, offer_price: Number(it.offer_price) || 0 })) };
      const { data } = await api.post(endpoint, body);
      onDone(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const terms = type === "consignment" ? cfg.terms_consign : cfg.terms_sell;
  return (
    <form onSubmit={submit} className="space-y-6" data-testid={`submit-form-${type}`}>
      <section className="rounded-2xl border bg-white p-4 sm:p-5 grid sm:grid-cols-3 gap-3">
        <L label="Nama lengkap *"><input className={field} value={f.name} onChange={u("name")} required minLength={2} data-testid="mk-name" /></L>
        <L label="Nomor WhatsApp *"><input className={field} value={f.phone} onChange={u("phone")} required placeholder="08xxxxxxxxxx" inputMode="tel" data-testid="mk-phone" /></L>
        <L label="Email (opsional)"><input type="email" className={field} value={f.email} onChange={u("email")} data-testid="mk-email" /></L>
        <input className="hidden" tabIndex={-1} autoComplete="off" value={f.website} onChange={u("website")} aria-hidden="true" />
      </section>
      {items.map((it, i) => (
        <ItemCard key={it.key} it={it} idx={i} type={type} cats={cfg.categories} uploadUrl={uploadUrl} canRemove={items.length > 1}
          set={(v) => setItems(items.map((x, j) => (j === i ? v : x)))} remove={() => setItems(items.filter((_, j) => j !== i))} />
      ))}
      {items.length < 20 && <button type="button" onClick={() => setItems([...items, blankItem()])} className="btn-brand w-full" data-testid="add-item-btn"><Plus className="h-4 w-4" />ADD ANOTHER ITEM</button>}
      <section className="rounded-2xl border bg-white p-4 sm:p-5 space-y-4">
        {type === "consignment" && (
          <L label="Masa penitipan *" hint={`Komisi default ${cfg.commission_default}% dari harga terjual (bisa berbeda per kategori).`}>
            <select className={field} value={f.consign_days} onChange={u("consign_days")} required data-testid="mk-consign-days"><option value="">Pilih masa titip</option>{cfg.durations.map((d) => <option key={d} value={d}>{d} hari</option>)}</select>
          </L>
        )}
        <div>
          <span className="text-sm font-semibold">{type === "consignment" ? "Metode penyerahan barang *" : "Metode penerimaan barang *"}</span>
          <div className="mt-2 grid sm:grid-cols-2 gap-3">
            {[["store", "Inspeksi / antar di toko", "Bawa barang ke toko BengkelHobby"], ["ship", "Kirim barang", "Kirim ke alamat penerimaan kami"]].map(([v, t, d]) => (
              <label key={v} className={`rounded-xl border-2 p-3 cursor-pointer ${f.handover === v ? "border-neutral-900 bg-[var(--bk-yellow)]/30" : "border-neutral-200"}`} data-testid={`mk-handover-${v}`}>
                <input type="radio" name="handover" value={v} checked={f.handover === v} onChange={u("handover")} className="mr-2 accent-[#ff7a00]" /><b>{t}</b><p className="text-xs text-neutral-600 ml-5">{d}</p>
              </label>
            ))}
          </div>
          {f.handover === "ship" && (
            <div className="mt-3 rounded-xl bg-sky-50 border border-sky-200 p-4 text-sm space-y-1" data-testid="mk-shipping-info">
              <div><b>Alamat penerimaan:</b> {cfg.receiving_address}</div>
              <div><b>Penerima:</b> {cfg.receiving_name} ({cfg.receiving_phone})</div>
              <div><b>Pengemasan:</b> {cfg.packing_instructions}</div>
              <div><b>Pengiriman:</b> {cfg.shipping_info}</div>
              <div><b>Biaya kirim:</b> {cfg.shipping_cost_terms}</div>
              <div className="text-xs text-sky-900">Tulis nomor pengajuan di paket. Nomor diberikan setelah pengajuan dikirim.</div>
            </div>
          )}
        </div>
        {!assisted && (
          <div>
            <div className="max-h-40 overflow-auto rounded-lg bg-neutral-50 border p-3 text-xs whitespace-pre-line" data-testid="mk-terms">{terms}</div>
            <label className="flex gap-2 items-start mt-2 text-sm"><input type="checkbox" checked={f.terms_accepted} onChange={u("terms_accepted")} required className="mt-1 accent-[#ff7a00]" data-testid="mk-terms-check" />Saya menyetujui syarat & ketentuan di atas dan menyatakan barang adalah milik saya.</label>
          </div>
        )}
        <button className="btn-ink w-full h-12" disabled={busy} data-testid="mk-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{type === "consignment" ? "KIRIM PENGAJUAN TITIP JUAL" : "KIRIM PENGAJUAN PENJUALAN"} ({items.length} item)</button>
      </section>
    </form>
  );
}

export default function SubmitPage() {
  const { kind } = useParams();
  const type = kind === "titip" ? "consignment" : "sell";
  const [res, setRes] = useState(null);
  const { data: cfg } = useQuery({ queryKey: ["mk-config"], queryFn: () => api.get("/collection/config").then((r) => r.data) });
  useSeo(type === "consignment" ? "Ajukan Titip Jual (Consignment)" : "Ajukan Penjualan (Sell to Us)", "Ajukan koleksi kamu ke BengkelHobby tanpa perlu membuat akun.");
  return (
    <div className="container-x py-10 max-w-4xl" data-testid={`submit-page-${type}`}>
      <Link to="/jual-koleksi" className="text-sm font-semibold underline">← Jual Koleksi Kamu</Link>
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">{type === "consignment" ? "Ajukan Titip Jual" : "Ajukan Penjualan ke BengkelHobby"}</h1>
      <p className="text-neutral-600 mt-1 mb-6">Tanpa login. Satu pengajuan bisa berisi banyak item. {type === "sell" && "Semua harga final ditentukan setelah barang diinspeksi."}</p>
      {res ? <Success res={res} wa={cfg?.whatsapp} /> : <SubmitFormCore type={type} onDone={(d) => { setRes(d); window.scrollTo(0, 0); }} />}
    </div>
  );
}
